import type { GetOrganizationsInGeoAreaTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet } from 'react-native';
import { Button, Checkbox, Text } from 'react-native-paper';

import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type TagValue = GetOrganizationsInGeoAreaTagsEnum;

type Props = {
  availableTags: TagValue[];
  initialSelectedTags: TagValue[];
  onConfirm: (tags: TagValue[]) => void;
};

export const TagPickerContent: FC<Props> = ({
  availableTags,
  initialSelectedTags,
  onConfirm
}) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-area.filters'
  });
  const { t: tTags } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.tagLabels'
  });

  const [selected, setSelected] = useState<TagValue[]>(initialSelectedTags);

  const toggle = useCallback((tag: TagValue) => {
    setSelected((prev) =>
      prev.includes(tag)
        ? prev.filter((value) => value !== tag)
        : [...prev, tag]
    );
  }, []);

  return (
    <ScrollView contentContainerStyle={styles.tagList}>
      {availableTags.map((tag) => (
        <TagPickerRow
          key={tag}
          label={tTags(tag)}
          selected={selected.includes(tag)}
          onToggle={() => toggle(tag)}
          accentColor={theme.colors.primary}
        />
      ))}
      <Button
        mode="contained"
        onPress={() => onConfirm(selected)}
        style={styles.confirmButton}
      >
        {t('confirm', { count: selected.length })}
      </Button>
    </ScrollView>
  );
};

type TagPickerRowProps = {
  label: string;
  selected: boolean;
  onToggle: () => void;
  accentColor: string;
};

const TagPickerRow: FC<TagPickerRowProps> = ({
  label,
  selected,
  onToggle,
  accentColor
}) => (
  <Pressable
    accessibilityRole="checkbox"
    accessibilityState={{ checked: selected }}
    onPress={onToggle}
    style={styles.tagRow}
  >
    {/* Android variant: the iOS variant renders no visible control when
        unchecked (checkmark glyph only). */}
    <Checkbox.Android
      status={selected ? 'checked' : 'unchecked'}
      color={accentColor}
    />
    <Text variant="bodyLarge" style={styles.tagLabel}>
      {label}
    </Text>
  </Pressable>
);

const styles = StyleSheet.create({
  tagList: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 4
  },
  tagRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6
  },
  tagLabel: {
    marginLeft: 4,
    flex: 1
  },
  confirmButton: {
    marginTop: 12
  }
});
